import { Sentry } from './sentry';
import { logEvent } from 'firebase/analytics';
import { analytics, db } from './firebase';
import { getAuth } from 'firebase/auth';
import { doc, setDoc, getDoc } from 'firebase/firestore';

export type Severity = 'S1' | 'S2' | 'S3' | 'S4'; // S1 highest
export type Impact = 'user-blocking' | 'data-loss' | 'degraded' | 'cosmetic';
export type Urgency = 'immediate' | '24h' | 'next-release';
export type Domain =
  | 'payments'
  | 'security'
  | 'uploads'
  | 'reports'
  | 'ui'
  | 'auth'
  | 'storage'
  | 'network';

// Daily usage tracking types
export interface DailyUsageCounters {
  views: Record<string, number>; // page views by page name
  actions: Record<string, number>; // actions by action name
  dwellMs: number; // total dwell time in milliseconds
  device: 'Desktop' | 'iOS' | 'Android';
  plan: string;
  uploadBytes: number; // total bytes uploaded
}

export interface AppErrorMeta {
  domain: Domain;
  code: string; // stable grouping key, e.g. STRIPE_CHECKOUT_FAIL
  severity: Severity;
  impact: Impact;
  urgency: Urgency;
  retryable?: boolean;
  httpStatus?: number;
  providerCode?: string; // stripe/firebase error.code
  device?: string; // make/model if known (from prior EXIF summary)
  userAction?: string; // 'checkout', 'upload', 'generate_report'
  extras?: Record<string, unknown>;
}

/**
 * Sanitizes extra data for analytics logging by removing sensitive information
 */
function sanitize(extras?: Record<string, unknown>): Record<string, unknown> {
  if (!extras) return {};

  const sanitized: Record<string, unknown> = {};
  const sensitiveKeys = ['password', 'token', 'secret', 'key', 'auth', 'credential'];

  for (const [key, value] of Object.entries(extras)) {
    const lowerKey = key.toLowerCase();
    if (sensitiveKeys.some(sensitive => lowerKey.includes(sensitive))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = '[OBJECT]'; // Avoid logging complex objects
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

/**
 * Gets user agent info for device tracking
 */
function getUA(): Record<string, any> {
  if (typeof window === 'undefined') return {};

  const ua = navigator.userAgent;
  const platform = navigator.platform;

  // Simple device detection
  const isMobile = /Mobi|Android/i.test(ua);
  const isTablet = /Tablet|iPad/i.test(ua);
  const device = isTablet ? 'tablet' : isMobile ? 'mobile' : 'desktop';

  // OS detection
  let os = 'unknown';
  if (platform.includes('Mac')) os = 'macos';
  else if (platform.includes('Win')) os = 'windows';
  else if (platform.includes('Linux')) os = 'linux';
  else if (/iPhone|iPad|iPod/i.test(ua)) os = 'ios';
  else if (/Android/i.test(ua)) os = 'android';

  // Browser detection
  let browser = 'unknown';
  if (ua.includes('Chrome') && !ua.includes('Edg')) browser = 'chrome';
  else if (ua.includes('Firefox')) browser = 'firefox';
  else if (ua.includes('Safari') && !ua.includes('Chrome')) browser = 'safari';
  else if (ua.includes('Edg')) browser = 'edge';

  return { device, os, browser };
}

/**
 * Gets current user plan from auth claims
 * Note: customClaims may not be available synchronously
 */
async function getPlan(): Promise<string> {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) return 'FREE';

    // Get fresh ID token to access claims
    const idToken = await currentUser.getIdToken(false);
    const payload = JSON.parse(atob(idToken.split('.')[1]));
    return payload.plan || 'FREE';
  } catch {
    return 'FREE';
  }
}

/**
 * Gets current date in YYYYMMDD format
 */
function getCurrentDateKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}${month}${day}`;
}

/**
 * Gets device type from user agent
 */
function getDeviceType(): 'Desktop' | 'iOS' | 'Android' {
  if (typeof window === 'undefined') return 'Desktop';

  const ua = navigator.userAgent;
  if (/Android/i.test(ua)) return 'Android';
  if (/iPhone|iPad|iPod/i.test(ua)) return 'iOS';
  return 'Desktop';
}

/**
 * In-memory cache for daily counters (flushed at page unload)
 */
let dailyCounters: DailyUsageCounters | null = null;
let dailyCountersDate: string | null = null;

/**
 * Gets or initializes daily counters for current user and date
 */
async function getDailyCounters(): Promise<DailyUsageCounters> {
  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return null as any; // Should not happen in authenticated context

  const currentDate = getCurrentDateKey();

  // If we already have counters for today, return them
  if (dailyCounters && dailyCountersDate === currentDate) {
    return dailyCounters;
  }

  // Load existing counters from Firestore
  const docRef = doc(db, 'usage_daily', currentDate, 'users', user.uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    dailyCounters = docSnap.data() as DailyUsageCounters;
  } else {
    // Initialize new counters
    const currentPlan = await getPlan();
    dailyCounters = {
      views: {},
      actions: {},
      dwellMs: 0,
      device: getDeviceType(),
      plan: currentPlan,
      uploadBytes: 0,
    };
  }

  dailyCountersDate = currentDate;
  return dailyCounters;
}

/**
 * Flushes daily counters to Firestore (called at page unload)
 */
async function flushDailyCounters(): Promise<void> {
  if (!dailyCounters || !dailyCountersDate) return;

  const auth = getAuth();
  const user = auth.currentUser;
  if (!user) return;

  try {
    const docRef = doc(db, 'usage_daily', dailyCountersDate, 'users', user.uid);
    await setDoc(docRef, dailyCounters, { merge: true });
  } catch (error) {
    console.warn('Failed to flush daily counters:', error);
  }
}

/**
 * Updates daily counters with new data
 */
function updateDailyCounters(updates: Partial<DailyUsageCounters>): void {
  if (!dailyCounters) return;

  // Update views
  if (updates.views) {
    Object.entries(updates.views).forEach(([page, count]) => {
      dailyCounters!.views[page] = (dailyCounters!.views[page] || 0) + count;
    });
  }

  // Update actions
  if (updates.actions) {
    Object.entries(updates.actions).forEach(([action, count]) => {
      dailyCounters!.actions[action] = (dailyCounters!.actions[action] || 0) + count;
    });
  }

  // Update other fields
  if (updates.dwellMs !== undefined) {
    dailyCounters.dwellMs += updates.dwellMs;
  }
  if (updates.uploadBytes !== undefined) {
    dailyCounters.uploadBytes += updates.uploadBytes;
  }
  if (updates.plan) {
    dailyCounters.plan = updates.plan;
  }
  if (updates.device) {
    dailyCounters.device = updates.device;
  }
}

/**
 * Sets up page unload listener to flush counters
 */
function setupUnloadFlush(): void {
  if (typeof window === 'undefined') return;

  // Only set up once
  if ((window as any)._dailyCountersFlushSetup) return;
  (window as any)._dailyCountersFlushSetup = true;

  window.addEventListener('beforeunload', () => {
    // Use sendBeacon-like approach for reliable flushing
    flushDailyCounters().catch(console.warn);
  });

  // Also flush on visibility change (when tab becomes hidden)
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      flushDailyCounters().catch(console.warn);
    }
  });
}

/**
 * Tracks feature-level usage events
 */
export async function trackUsage(event: string, data: Record<string, any> = {}) {
  try {
    if (!analytics) return;

    const plan = await getPlan();
    logEvent(analytics, event, sanitize({ ...data, plan, ua: getUA() }));

    // Also update daily counters
    await updateUsageCounters(event, data);
  } catch (error) {
    console.warn('Failed to track usage:', error);
  }
}

/**
 * Updates daily usage counters based on event type
 */
async function updateUsageCounters(event: string, data: Record<string, any> = {}): Promise<void> {
  try {
    const auth = getAuth();
    if (!auth.currentUser) return;

    // Ensure counters are initialized
    await getDailyCounters();
    setupUnloadFlush();

    // Update counters based on event type
    if (event === 'usage_page_view') {
      const page = data.page || 'unknown';
      updateDailyCounters({ views: { [page]: 1 } });
    } else if (event === 'usage_action') {
      const action = data.action || 'unknown';
      updateDailyCounters({ actions: { [action]: 1 } });
    } else if (event === 'usage_dwell') {
      updateDailyCounters({ dwellMs: data.ms || 0 });
    } else if (event === 'usage_upload_bytes') {
      updateDailyCounters({ uploadBytes: data.bytes || 0 });
    }
  } catch (error) {
    console.warn('Failed to update usage counters:', error);
  }
}

/**
 * Starts dwell time tracking for a page
 * Returns a stop function that logs the dwell time when called
 */
export function startDwellTimer(page: string): () => void {
  const startTime = Date.now();

  return () => {
    const durationMs = Date.now() - startTime;
    trackUsage('usage_dwell', { page, ms: durationMs }).catch(console.warn);
  };
}

/**
 * Checks fresh ID token claims for write entitlements
 * Throws an error if user doesn't have write access
 */
export async function assertWriteAccess(): Promise<void> {
  try {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    // Get fresh ID token
    const idToken = await currentUser.getIdToken(false);
    const payload = JSON.parse(atob(idToken.split('.')[1]));

    if (!payload.hasPro) {
      throw new Error('Write access denied: PRO plan required');
    }
  } catch (error) {
    trackError(
      'Write access check failed',
      createErrorMeta('security', 'SEC_CLAIMS_OUTDATED', 'S2', 'user-blocking', 'immediate', {
        userAction: 'write_operation',
      }),
      error
    );
    throw error;
  }
}

/**
 * Tracks application errors with consistent taxonomy to Sentry and Firebase Analytics
 */
export function trackError(msg: string, meta: AppErrorMeta, err?: unknown) {
  Sentry.withScope(scope => {
    Object.entries(meta).forEach(([k, v]) => scope.setTag(k, String(v)));
    scope.setFingerprint([meta.domain, meta.code]);
    scope.setLevel(
      meta.severity === 'S1'
        ? 'fatal'
        : meta.severity === 'S2'
          ? 'error'
          : meta.severity === 'S3'
            ? 'warning'
            : 'info'
    );
    scope.setExtra('extras', meta.extras ?? {});
    Sentry.captureException(err ?? new Error(msg));
  });

  // Firebase Analytics reporting
  if (analytics) {
    logEvent(analytics, 'app_error', {
      domain: meta.domain,
      code: meta.code,
      severity: meta.severity,
      impact: meta.impact,
      urgency: meta.urgency,
      retryable: !!meta.retryable,
      http_status: meta.httpStatus ?? 0,
      provider_code: meta.providerCode ?? '',
      user_action: meta.userAction ?? '',
      ...sanitize(meta.extras),
    });
  }
}

/**
 * Predefined error codes for consistent grouping
 */
export const ERROR_CODES = {
  // Payment errors
  PAYMENT_CHECKOUT_CREATE_FAIL: 'PAYMENT_CHECKOUT_CREATE_FAIL',
  PAYMENT_PORTAL_CREATE_FAIL: 'PAYMENT_PORTAL_CREATE_FAIL',
  PAYMENT_CANCEL_FAIL: 'PAYMENT_CANCEL_FAIL',
  PAYMENT_WEBHOOK_VERIFY_FAIL: 'PAYMENT_WEBHOOK_VERIFY_FAIL',
  PAYMENT_ENTITLEMENT_DRIFT: 'PAYMENT_ENTITLEMENT_DRIFT',

  // Security errors
  SEC_RULE_DENIED_WRITE: 'SEC_RULE_DENIED_WRITE',
  SEC_ROUTE_BYPASS_BLOCKED: 'SEC_ROUTE_BYPASS_BLOCKED',
  SEC_CLAIMS_OUTDATED: 'SEC_CLAIMS_OUTDATED',

  // Upload errors
  UPLOAD_STALLED: 'UPLOAD_STALLED',
  UPLOAD_FAIL_RETRYED: 'UPLOAD_FAIL_RETRYED',
  UPLOAD_EXIF_PARSE_FAIL: 'UPLOAD_EXIF_PARSE_FAIL',

  // Data integrity errors
  DATA_CORRUPTION: 'DATA_CORRUPTION',
  DATA_VALIDATION_FAIL: 'DATA_VALIDATION_FAIL',

  // Export errors
  EXPORT_BUILD_FAIL: 'EXPORT_BUILD_FAIL',
  EXPORT_SIGN_URL_FAIL: 'EXPORT_SIGN_URL_FAIL',

  // Report errors
  REPORT_GEN_FAIL: 'REPORT_GEN_FAIL',

  // Auth errors
  AUTH_CLAIMS_REFRESH_FAIL: 'AUTH_CLAIMS_REFRESH_FAIL',
  AUTH_TOKEN_INVALID: 'AUTH_TOKEN_INVALID',

  // Network errors
  NETWORK_TIMEOUT: 'NETWORK_TIMEOUT',
  NETWORK_OFFLINE: 'NETWORK_OFFLINE',

  // Archive payment errors
  PAYMENT_ARCHIVE_BEGIN_FAIL: 'PAYMENT_ARCHIVE_BEGIN_FAIL',
  PAYMENT_SUB_CANCEL_FAIL: 'PAYMENT_SUB_CANCEL_FAIL',
  PAYMENT_ARCHIVE_WEBHOOK_MISMATCH: 'PAYMENT_ARCHIVE_WEBHOOK_MISMATCH',
  PAYMENT_PROMO_APPLY_FAIL: 'PAYMENT_PROMO_APPLY_FAIL',
} as const;

/**
 * Helper function to create error metadata with defaults
 */
export function createErrorMeta(
  domain: Domain,
  code: string,
  severity: Severity,
  impact: Impact,
  urgency: Urgency,
  overrides: Partial<AppErrorMeta> = {}
): AppErrorMeta {
  return {
    domain,
    code,
    severity,
    impact,
    urgency,
    ...overrides,
  };
}
